import React, { createContext, useState, useEffect } from 'react';
import { node } from 'prop-types';
import createAnalyticsClient from '../analytics/client';
import initNewRelic from '../analytics/newrelic';
import { useNavOptionsContext } from './optionsContext';

/**
 * @typedef {object} AnalyticsContextType
 * @property {object} newRelic
 * @property {object} analyticsClient
 */

/**
 * Analytics context
 * @type {React.Context<AnalyticsContextType>}
 */
const AnalyticsContext = createContext({ analyticsClient: null, newRelic: null });

/**
 * Creates newrelic and click analytics context for use throughout the app.
 * It is possible that the V3 code could start firing events
 * before the NikeDotcomNav is mounted which could lead to dropped events.
 * If this is the case we will need a queue or to halt hydration until we have what we need.
 * @param {object} children components
 * @returns {React.ReactElement} react children wrapped in AnalyticsContext provider
 */
const AnalyticsContextProvider = ({ children }) => {
  const [analyticsClient, setAnalyticsClient] = useState(null);
  const [newRelic, setNewRelic] = useState();
  const { mounted } = useNavOptionsContext();

  // Client side execution only due to it being a useEffect.
  useEffect(() => {
    if (mounted) {
      const instance = window.NikeDotcomNav;
      const newRelicEvents = initNewRelic(instance);
      setNewRelic(newRelicEvents);

      const analyticsClient = createAnalyticsClient(window, instance);
      setAnalyticsClient(analyticsClient);
    }
  }, [mounted]);

  return (
    <AnalyticsContext.Provider value={{ newRelic, analyticsClient }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * @returns {AnalyticsContextType} analytics context
 */
function useAnalyticsContext() {
  return React.useContext(AnalyticsContext);
}

AnalyticsContextProvider.propTypes = {
  children: node,
};

export { AnalyticsContext, AnalyticsContextProvider, useAnalyticsContext };
